import { getBackendLanguage } from '@assets/i18n/config';
import {
  LocalizationProvider,
  MobileDatePicker,
  type MobileDatePickerProps,
  MobileDateTimePicker,
  type MobileDateTimePickerProps,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { UserContext } from '@providers/userContextProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

/** Wraps MUI LocalizationProvider with locale from i18n so calendar headers (e.g. "février 2026") display in the user's language */
export const LocalizedDatePickerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslation('components');
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={getBackendLanguage()}
      localeText={{
        cancelButtonLabel: t('datePicker.cancelButtonLabel'),
        okButtonLabel: t('datePicker.okButtonLabel'),
        datePickerDefaultToolbarTitle: t(
          'datePicker.datePickerDefaultToolbarTitle',
        ),
        dateTimePickerDefaultToolbarTitle: t(
          'datePicker.dateTimePickerDefaultToolbarTitle',
        ),
      }}
    >
      {children}
    </LocalizationProvider>
  );
};

type LocalizedDatePickerProps = Omit<
  MobileDatePickerProps<any, any>,
  'inputFormat' | 'toolbarFormat'
> & { customFormat?: string };
/**
 * Date picker that observes organization's localization settings
 */
export const LocalizedDatePicker = (props: LocalizedDatePickerProps) => {
  const { activeOrganization } = useContext(UserContext);
  const format =
    activeOrganization.applicableLocalization?.dateFormat ?? 'YYYY-MM-DD';

  return (
    <MobileDatePicker
      inputFormat={props.customFormat ?? format}
      toolbarFormat={props.customFormat ?? format}
      {...props}
    />
  );
};

type LocalizedDateTimePickerProps = Omit<
  MobileDateTimePickerProps<any, any>,
  'inputFormat' | 'toolbarFormat'
>;

/**
 * Date+Time picker that observes organization's localization settings
 */
export const LocalizedDateTimePicker = (
  props: LocalizedDateTimePickerProps,
) => {
  const { activeOrganization } = useContext(UserContext);
  const dateFormat =
    activeOrganization.applicableLocalization?.dateFormat ?? 'YYYY-MM-DD';
  const format = `${dateFormat}  hh:mma`;

  return (
    <MobileDateTimePicker
      inputFormat={format}
      toolbarFormat={format}
      {...props}
    />
  );
};
